/**
 * Brand assets — the operator's logo, and the app's splash screen.
 *
 * Two assets, two sets of rules, two independent loads. The logo is a small raster mark
 * shown across the console and in operator email; the splash is what a customer sees while
 * the app is starting, full-bleed on a phone, and may be a short video.
 *
 * **Each card loads and fails on its own.** The page used to return early on the logo's
 * read, which would have meant a splash the API could not describe blanking the logo upload
 * control beside it. Two cards, two `useAsync`es, no shared early return.
 *
 * **A null asset is the normal starting state, not a failure.** Before the first upload the
 * API has nothing to describe and answers 404, which `requestOptional` maps to null (see
 * `client.ts`) precisely so these cards render their upload controls rather than an error
 * box. The logo page used to render the error box instead — on exactly the fresh deployment
 * where the upload control was the only thing anyone wanted. The splash read is wired the
 * same way from the start.
 *
 * **The client-side checks below are a courtesy, not the rule.** The server is the authority
 * on every one of them: it decodes the bytes, sniffs their magic number against the declared
 * mime, enforces the byte ceiling, and reads the dimensions out of the file header. What the
 * browser does here is spare the operator a multi-megabyte round-trip to be told something a
 * `File` object already knows. Nothing here relaxes a server rule, and nothing here may be
 * trusted in place of one.
 *
 * Gated on `admins:write` (superadmin).
 */
import { useEffect, useRef, useState } from 'react';

import { API_BASE_URL } from '../api/config.js';
import { messageOf } from '../api/errors.js';
import { useAuth } from '../auth/AuthContext.js';
import { adminCan } from '../contracts/permissions.js';
import type {
  BrandAssetMime,
  BrandHeroSurface,
  SplashAsset,
  SplashAssetMime,
} from '../contracts/types.js';
import { Badge, Button, Card, ErrorBox, LoadingState } from '../components/ui.js';
import { AssetFailure, useAssetFailure } from '../components/AssetFailure.js';
import { IconAlert, IconCheck, IconFilm, IconImage } from '../components/icons.js';
import { useToast } from '../components/Toast.js';
import { useAsync } from '../hooks/useAsync.js';
import { assetUrl } from '../lib/assetUrl.js';
import { measureHeroLegibility, type HeroLegibility } from '../lib/heroLegibility.js';
import { relativeTime } from '../lib/format.js';

const MAX_BYTES = 1024 * 1024;

/** The image mimes the contract accepts for a brand asset. Mirrors `BRAND_ASSET_MIMES`. */
const ALLOWED_MIMES = ['image/png', 'image/jpeg', 'image/webp'] as const satisfies readonly BrandAssetMime[];

/**
 * What a splash may be. Mirrors `SPLASH_ASSET_MIMES`: the raster set **plus** `video/mp4`.
 *
 * A separate list from the logo's rather than a widening of it, exactly as the contract keeps
 * them separate — the logo, the bank marks and the banner artwork all ride the raster list,
 * and none of them has any business being a video. `image/svg+xml` is absent from both for
 * the same reason: an SVG is an active document that can carry script, and these bytes are
 * later served from a public route and rendered.
 */
const SPLASH_MIMES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'video/mp4',
] as const satisfies readonly SplashAssetMime[];

/**
 * `SPLASH_ASSET_MAX_BYTES` from the API, in decoded bytes.
 *
 * Six megabytes, and the figure is the API's to change: the blob travels to MySQL in a single
 * packet and base64 inflates it by a third, so 6 MB of file is 8 MB on the wire against a
 * 16 MB packet limit. Repeated here only so an operator learns their 40 MB video is too large
 * before uploading it, not after.
 */
const SPLASH_MAX_BYTES = 6_000_000;

function isAllowedMime(mime: string): mime is BrandAssetMime {
  return (ALLOWED_MIMES as readonly string[]).includes(mime);
}

function isSplashMime(mime: string): mime is SplashAssetMime {
  return (SPLASH_MIMES as readonly string[]).includes(mime);
}

/** Read a File as bare base64 (no `data:` prefix), for the upload body. */
function toBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

/**
 * A byte count an operator can compare against the ceiling.
 *
 * Decimal megabytes, not mebibytes, because `SPLASH_ASSET_MAX_BYTES` is 6,000,000 and a file
 * manager reporting "5.8 MB" against a limit stated in MiB is a support question. Small files
 * fall back to kB rather than rendering as `0.00 MB`.
 */
function megabytes(bytes: number): string {
  if (bytes < 100_000) return `${Math.max(1, Math.round(bytes / 1000))} kB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

export function BrandAssetsPage(): JSX.Element {
  const { session } = useAuth();
  const canWrite = session !== null && adminCan(session.me.role, 'admins:write');

  return (
    <>
      <div className="page-head">
        <h1 className="page-head__title">Brand assets</h1>
        <p className="page-head__sub">
          The four images customers see. Logo is your wordmark, icon the small square mark, splash the start-up screen, and the two heroes sit behind the balance headers.
          {!canWrite && ' Your role can view but not change branding.'}
        </p>
      </div>

      <LogoSection canWrite={canWrite} />
      <IconSection canWrite={canWrite} />
      <HeroSection canWrite={canWrite} surface="naira" />
      <HeroSection canWrite={canWrite} surface="crypto" />
      <SplashSection canWrite={canWrite} />
    </>
  );
}

// ── Icon ───────────────────────────────────────────────────────────────────────────

/**
 * The brand icon.
 *
 * Its own card and its own asset, not a crop of the logo. A logo is a wordmark and is wide;
 * an icon is square. Every surface that wants one wants the square version, and asking a
 * wordmark to serve as a favicon produces either an unreadable strip or a badly cropped
 * letter, depending entirely on which file the operator happened to upload.
 */
function IconSection({ canWrite }: { canWrite: boolean }): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.brandIcon(), []);

  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    return () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function choose(file: File | undefined) {
    if (file === undefined) return;
    if (!isAllowedMime(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP icon.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File too large', 'Keep the icon under 1 MB.');
      return;
    }
    if (preview !== null) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setFileName(file.name);
  }

  async function upload() {
    const file = inputRef.current?.files?.[0];
    if (file === undefined || !isAllowedMime(file.type)) {
      toast.info('Choose a file first');
      return;
    }
    setUploading(true);
    try {
      const dataBase64 = await toBase64(file);
      const updated = await api.uploadBrandIcon({ mime: file.type, dataBase64 });
      state.setData(() => updated);
      toast.success('Icon uploaded', 'The app icon has been updated.');
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setUploading(false);
    }
  }

  if (state.loading) return <LoadingState label="Loading the brand icon" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const current = state.data;
  const stored = current === null ? null : assetUrl(API_BASE_URL, '/config/icon', current.updatedAt);

  return (
    <div className="grid grid--2">
      <Card title="App icon">
        <p className="muted" style={{ marginTop: -4 }}>
          Square. Used as the favicon, as the avatar on a GenEx-to-GenEx transfer in activity,
          and as the notification icon. Upload it at the largest square size you have; it is
          scaled down for each surface.
        </p>

        <div
          className="dropzone"
          role="button"
          tabIndex={0}
          onClick={() => canWrite && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && canWrite) inputRef.current?.click();
          }}
        >
          <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
            <IconImage size={30} />
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
              {fileName ?? 'Drop an icon here, or click to browse'}
            </div>
            <div className="faint" style={{ fontSize: 12 }}>PNG · JPG · WebP · max 1 MB</div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={!canWrite}
            onChange={(e) => choose(e.target.files?.[0])}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Button
            variant="primary"
            disabled={!canWrite || preview === null}
            loading={uploading}
            onClick={upload}
          >
            Upload icon
          </Button>
        </div>

        <div className="kv" style={{ marginTop: 20 }}>
          <dt>Stored asset</dt>
          {current === null ? (
            <dd className="faint">Nothing uploaded yet. Every surface falls back to a plain mark.</dd>
          ) : (
            <dd className="mono" style={{ fontSize: 12 }}>{current.key}</dd>
          )}
          {current !== null && (
            <>
              <dt>Updated</dt>
              <dd>{relativeTime(current.updatedAt)}</dd>
            </>
          )}
        </div>
      </Card>

      <Card title="Icon preview">
        {/*
          Three states, not two. "Nothing uploaded" and "an icon is stored but this console has
          no API origin to fetch it from" used to render the same sentence, so a mock build told
          an operator their icon did not exist. The splash card below has always drawn that
          distinction; this one now does too.
        */}
        {preview === null && stored === null ? (
          current === null ? (
            <div className="faint" style={{ padding: '20px 0' }}>
              No icon yet. Upload one to see it at the sizes the app actually draws it.
            </div>
          ) : (
            <div className="faint" style={{ padding: '20px 0' }}>
              An icon is stored, but this console is running on its in-memory demo backend, so
              there are no bytes to fetch. Pick a file to preview one.
            </div>
          )
        ) : (
          <IconSizes src={preview ?? stored ?? ''} />
        )}
      </Card>
    </div>
  );
}

/**
 * The stored icon at the four sizes the app draws it, or one statement that it would not load.
 *
 * One `failed` flag for all four, because all four are the same file at the same URL: four
 * copies of the failure panel stacked in a row would be four ways of saying one thing. And
 * one empty square is ambiguous where one sentence is not — an icon that draws nothing looks
 * exactly like an icon nobody uploaded, which is precisely the confusion an operator reported.
 */
function IconSizes({ src }: { src: string }): JSX.Element {
  const { failed, onError } = useAssetFailure(src);

  if (failed) return <AssetFailure label="The app icon" src={src} />;

  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 20, padding: '12px 0' }}>
      {[64, 40, 24, 16].map((size) => (
        <div key={size} style={{ textAlign: 'center' }}>
          <img
            src={src}
            alt={`Icon at ${String(size)} pixels`}
            width={size}
            height={size}
            style={{ borderRadius: size >= 40 ? 10 : 4, objectFit: 'cover' }}
            onError={onError}
          />
          <div className="faint" style={{ fontSize: 11, marginTop: 6 }}>{size}px</div>
        </div>
      ))}
    </div>
  );
}

// ── Balance-header heroes ──────────────────────────────────────────────────────────

const HERO_MAX_BYTES = 3_000_000;

/**
 * One balance header's background.
 *
 * Any image works: the server records what it actually measured and the app cover-fits to
 * 16:9, cropping the overflow rather than stretching. An image far off that shape is accepted
 * with a note, not refused. A field that only takes one exact pixel size is a field an
 * operator cannot fill without an image editor, so it stays empty and the feature ships as
 * decoration nobody uses.
 */
function HeroSection({
  canWrite,
  surface,
}: {
  canWrite: boolean;
  surface: BrandHeroSurface;
}): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.brandHero(surface), [surface]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * What the chosen image does to the balance sitting on it.
   *
   * Null until the preview image has actually decoded, and null again when the browser will not
   * give up its pixels. Both mean "no advice" rather than "it is fine": showing a reassurance
   * this has not measured would be worse than showing nothing.
   */
  const [legibility, setLegibility] = useState<HeroLegibility | null>(null);
  /**
   * Whether the browser refused the picture outright.
   *
   * Distinct from `legibility === null`, which this used to be the only signal for. "Could not
   * measure the pixels" and "could not load the file" are different failures with different
   * answers, and folding them together is what let a header the console could not fetch at all
   * render as a black 16:9 rectangle under a scrim, indistinguishable from a dark photograph.
   *
   * `crossOrigin="anonymous"` below widens what lands here: it makes the fetch a CORS request,
   * so a stored image the API will not share with this origin becomes a load failure rather
   * than a silently tainted canvas.
   *
   * Keyed on both sources the preview can draw from, because the hook resets on a changed key
   * and both a fresh local pick and a completed upload have to count as a fresh attempt. The
   * URL itself is not available this early — it is built after the loading guard below.
   */
  const { failed, onError: onPreviewError } = useAssetFailure(
    `${preview ?? ''}|${state.data?.updatedAt ?? ''}`,
  );

  useEffect(() => {
    return () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const label = surface === 'naira' ? 'Naira balance header' : 'Crypto balance header';

  function choose(file: File | undefined) {
    if (file === undefined) return;
    if (!isAllowedMime(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > HERO_MAX_BYTES) {
      toast.error('File too large', `Keep it under ${megabytes(HERO_MAX_BYTES)}.`);
      return;
    }
    if (preview !== null) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setFileName(file.name);
    // Cleared, so advice about the previous image cannot sit under the new one for the frames
    // before it decodes. Stale advice about a picture nobody is looking at is worse than none.
    setLegibility(null);
  }

  /*
   * Save the text-colour choice.
   *
   * The response replaces local state rather than the click doing so optimistically: the two
   * differ the moment a save fails, and a control that keeps showing the value somebody clicked
   * is how they conclude the setting does not stick.
   */
  async function pickTextColor(textColor: 'auto' | 'light' | 'dark') {
    setBusy(true);
    try {
      const updated = await api.setBrandHeroTextColor(surface, { textColor });
      state.setData(() => updated);
      toast.success(
        'Saved',
        textColor === 'auto'
          ? 'Text over this header follows the measurement.'
          : `Text over this header is ${textColor === 'light' ? 'white' : 'black'}.`,
      );
    } catch (err) {
      toast.error('Could not save that', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  }

  async function upload() {
    const file = inputRef.current?.files?.[0];
    if (file === undefined || !isAllowedMime(file.type)) {
      toast.info('Choose a file first');
      return;
    }
    setBusy(true);
    try {
      const dataBase64 = await toBase64(file);
      const updated = await api.uploadBrandHero(surface, { mime: file.type, dataBase64 });
      state.setData(() => updated);
      if (updated.aspectWarning === null) {
        toast.success('Background uploaded', `${label} updated.`);
      } else {
        toast.info('Background uploaded', updated.aspectWarning);
      }
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await api.clearBrandHero(surface);
      state.setData(() => null);
      toast.success('Background removed', `${label} is back to a plain background.`);
    } catch (err) {
      toast.error('Could not remove it', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  if (state.loading) return <LoadingState label={`Loading the ${label.toLowerCase()}`} />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const current = state.data;
  const stored =
    current === null ? null : assetUrl(API_BASE_URL, `/config/hero/${surface}`, current.updatedAt);
  const shown = preview ?? stored;

  return (
    <div className="grid grid--2">
      <Card title={label}>
        <p className="muted" style={{ marginTop: -4 }}>
          Any photograph works. It is cropped to fill a 16:9 band, so keep the subject near the
          middle. Landscape reads best.
        </p>

        <div
          className="dropzone"
          role="button"
          tabIndex={0}
          onClick={() => canWrite && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && canWrite) inputRef.current?.click();
          }}
        >
          <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
            <IconImage size={30} />
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
              {fileName ?? 'Drop an image here, or click to browse'}
            </div>
            <div className="faint" style={{ fontSize: 12 }}>
              PNG · JPG · WebP · max {megabytes(HERO_MAX_BYTES)}
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={!canWrite}
            onChange={(e) => choose(e.target.files?.[0])}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Button
            variant="primary"
            disabled={!canWrite || preview === null}
            loading={busy}
            onClick={upload}
          >
            Upload background
          </Button>
          {current !== null && (
            <Button variant="ghost" disabled={!canWrite} loading={busy} onClick={clear}>
              Remove
            </Button>
          )}
        </div>

        {current !== null && (
          <div className="kv" style={{ marginTop: 20 }}>
            <dt>Stored image</dt>
            <dd>
              {current.width}x{current.height}, {megabytes(current.bytes)}
            </dd>
            <dt>Updated</dt>
            <dd>{relativeTime(current.updatedAt)}</dd>
          </div>
        )}

        {/*
          Advice, in the amber the advice below already uses — not `error-box`.

          `aspectWarning` is a note and never a refusal: the upload succeeded, the bytes are
          stored, and the client cover-fits the image. Dressed in the red error style it read as
          a rejection, and operators duly reported that the header "would not upload". a12431a
          already softened the toast to `toast.info` for exactly that reason; this panel is the
          half that kept saying it, on every page load rather than once.

          Same argument as `.hero-advice`'s own comment: an operator has reasons for a piece of
          artwork this code knows nothing about, and a red box over a picture they are allowed
          to use reads as a bug in the console.
        */}
        {current?.aspectWarning != null && (
          <div className="hero-advice hero-advice--warn" role="note">
            <IconAlert size={16} />
            <div>{current.aspectWarning}</div>
          </div>
        )}
      </Card>

      <Card title="Header preview">
        {shown === null ? (
          <div className="faint" style={{ padding: '20px 0' }}>
            {current === null
              ? 'No background yet. The header renders on its plain brand colour.'
              : 'A background is stored, but this console is running on its in-memory demo backend, so there are no bytes to fetch. Pick a file to preview one.'}
          </div>
        ) : failed ? (
          // Outside the framed preview deliberately. The frame lays a dark scrim over whatever
          // it contains, so a failure panel drawn inside it would be a red box nobody can read.
          <AssetFailure label={label} src={shown} />
        ) : (
          <div
            style={{
              position: 'relative',
              aspectRatio: '16 / 9',
              borderRadius: 12,
              overflow: 'hidden',
            }}
          >
            <img
              src={shown}
              alt={`${label} background`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              /*
               * Measured on load rather than on selection, because there are no pixels to read
               * until the browser has decoded the file. `crossOrigin` is what keeps the canvas
               * untainted for the *stored* image, which is fetched from the API's origin; a
               * blob URL from a local file needs nothing and ignores it.
               */
              crossOrigin="anonymous"
              onLoad={(e) => setLegibility(measureHeroLegibility(e.currentTarget))}
              onError={() => {
                setLegibility(null);
                onPreviewError();
              }}
            />
            {/* The same treatment the app uses: a dark scrim so white figures stay readable
                over an arbitrary photograph. Previewing without it would show an operator a
                picture that looks fine and a balance they cannot read. */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.55))',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                padding: 18,
                /*
                 * The colour the measurement recommends, so the preview shows what the customer
                 * would actually see rather than always assuming white. Before the image has
                 * decoded there is nothing to go on and white is the app's own default.
                 */
                color: legibility?.recommended === 'dark' ? '#111111' : '#FFFFFF',
              }}
            >
              <div style={{ fontSize: 12, opacity: 0.85 }}>
                {surface === 'naira' ? 'Naira balance' : 'Assets'}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>
                {surface === 'naira' ? '\u20A674,310.55' : '\u20A621,319,183.24'}
              </div>
            </div>
          </div>
        )}

        {/*
          The verdict, under the picture it is about.

          Shown only once something has been measured. An operator who has not chosen an image,
          or whose image the browser would not let us read, gets nothing rather than a
          reassurance nothing earned.
        */}
        {shown !== null && legibility !== null && (
          <div
            className={legibility.passes ? 'hero-advice' : 'hero-advice hero-advice--warn'}
            role="note"
          >
            {legibility.passes ? <IconCheck size={16} /> : <IconAlert size={16} />}
            <div>
              <div style={{ fontWeight: 600 }}>
                {legibility.recommended === 'light'
                  ? 'White text on this one'
                  : 'Dark text on this one'}
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
                {legibility.advice}
              </div>
            </div>
          </div>
        )}

        {/*
          The choice, which is the operator's rather than the measurement's.

          Shown whenever a header exists and independent of whether the browser could measure
          it: a picture we could not read is exactly the case where the person looking at it has
          to decide. Legibility over a photograph is not a property of its average brightness -
          a header dark everywhere except the top-right corner, which is where the balance sits,
          measures "use white" and is unreadable.
        */}
        {current !== null && (
          <div className="hero-textpick">
            <span className="muted" style={{ fontSize: 12.5 }}>
              Text over this header
            </span>
            <div
              className="hero-textpick__row"
              role="group"
              aria-label="Text colour over this header"
            >
              {(['auto', 'light', 'dark'] as const).map((choice) => (
                <button
                  key={choice}
                  type="button"
                  className={
                    current.textColor === choice
                      ? 'btn btn--sm btn--primary'
                      : 'btn btn--sm btn--ghost'
                  }
                  disabled={busy}
                  aria-pressed={current.textColor === choice}
                  onClick={() => void pickTextColor(choice)}
                >
                  {choice === 'auto' ? 'Automatic' : choice === 'light' ? 'White' : 'Black'}
                </button>
              ))}
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Automatic follows the measurement above. Choose one yourself when the balance sits
              over a lighter or darker part of the picture than the rest of it.
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Logo ───────────────────────────────────────────────────────────────────────────

function LogoSection({ canWrite }: { canWrite: boolean }): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.brandLogo(), []);

  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    return () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function choose(file: File | undefined) {
    if (file === undefined) return;
    if (!isAllowedMime(file.type)) {
      toast.error('Unsupported image', 'Choose a PNG, JPG or WebP logo.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File too large', 'Keep the logo under 1 MB.');
      return;
    }
    if (preview !== null) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    setFileName(file.name);
  }

  async function upload() {
    const file = inputRef.current?.files?.[0];
    if (file === undefined || !isAllowedMime(file.type)) {
      toast.info('Choose a file first');
      return;
    }
    setUploading(true);
    try {
      const dataBase64 = await toBase64(file);
      const updated = await api.uploadBrandLogo({ mime: file.type, dataBase64 });
      state.setData(() => updated);
      toast.success('Logo uploaded', 'Your operator branding has been updated.');
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setUploading(false);
    }
  }

  if (state.loading) return <LoadingState label="Loading the operator logo…" />;
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const current = state.data;
  const stored = current === null ? null : assetUrl(API_BASE_URL, '/config/logo', current.updatedAt);
  const shown = preview ?? stored;

  return (
    <div className="grid grid--2">
      <Card title="Operator logo">
        <div
          className={`dropzone${dragOver ? ' dropzone--over' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => canWrite && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && canWrite) inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (canWrite) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (canWrite) choose(e.dataTransfer.files[0]);
          }}
        >
          <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
            <IconImage size={30} />
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
              {fileName ?? 'Drop a logo here, or click to browse'}
            </div>
            <div className="faint" style={{ fontSize: 12 }}>PNG · JPG · WebP · max 1 MB</div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            disabled={!canWrite}
            onChange={(e) => choose(e.target.files?.[0])}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <Button variant="primary" disabled={!canWrite || preview === null} loading={uploading} onClick={upload}>
            Upload logo
          </Button>
          {preview !== null && (
            <Button
              variant="ghost"
              onClick={() => {
                if (preview !== null) URL.revokeObjectURL(preview);
                setPreview(null);
                setFileName(null);
                if (inputRef.current) inputRef.current.value = '';
              }}
            >
              Clear
            </Button>
          )}
        </div>

        {current === null ? (
          <div className="kv" style={{ marginTop: 20 }}>
            <dt>Stored asset</dt>
            <dd className="faint">
              Nothing uploaded yet. Pick a file above to set the operator logo.
            </dd>
          </div>
        ) : (
          <div className="kv" style={{ marginTop: 20 }}>
            <dt>Stored asset</dt>
            <dd className="mono" style={{ fontSize: 12 }}>{current.key}</dd>
            <dt>Format</dt>
            <dd>
              <Badge tone="neutral">{current.mime.replace('image/', '').toUpperCase()}</Badge>
            </dd>
            <dt>Updated</dt>
            <dd>{relativeTime(current.updatedAt)}</dd>
          </div>
        )}
      </Card>

      <Card title="Logo preview">
        {/*
          The stored logo, not only a freshly chosen one.

          This card used to render nothing but the local `preview`, so an operator who uploaded
          a logo and then reloaded the page was shown the sentence "The current logo is stored
          on the server" and no picture — on the one screen whose job is to show them the
          picture. That is a large part of "images uploaded do not render": there was no image
          element here at all to fail.

          The bytes come from the public `/config/logo`, which is what the app, the website and
          this console's own sidebar read, so what is on screen is what everyone else sees
          rather than a second copy that could disagree.
        */}
        {shown === null ? (
          <div className="faint" style={{ padding: '20px 0' }}>
            {current === null
              ? 'No logo yet. Upload one to see it previewed on light and dark surfaces.'
              : 'A logo is stored, but this console is running on its in-memory demo backend, so there are no bytes to fetch. Choose a file to preview one.'}
          </div>
        ) : (
          <LogoOnBothGrounds src={shown} />
        )}
      </Card>
    </div>
  );
}

/**
 * The logo on light and on dark, or one statement that it would not load.
 *
 * One `failed` flag across both grounds: they are the same file, so two failure panels would be
 * one fact said twice, and two empty frames would be the ambiguity this replaces.
 *
 * A stored logo that will not load has a second, specific cause worth knowing about, and the
 * panel's advice to open the URL is what surfaces it: `/config/logo` serves the asset the
 * *platform settings* point at, not whatever bytes sit under the `logo` key. Uploading now sets
 * that pointer in the same transaction as the bytes, but a logo stored before it did, or one
 * whose pointer was later cleared from Business Settings, is bytes nothing references. So a 404
 * here can mean "stored but not adopted" rather than "lost", and re-uploading once fixes it.
 */
function LogoOnBothGrounds({ src }: { src: string }): JSX.Element {
  const { failed, onError } = useAssetFailure(src);

  if (failed) return <AssetFailure label="The operator logo" src={src} />;

  return (
    <div className="logo-preview">
      <div>
        <div className="logo-preview__frame">
          <img src={src} alt="Logo on light" onError={onError} />
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 6, textAlign: 'center' }}>On light</div>
      </div>
      <div>
        <div className="logo-preview__frame logo-preview__frame--dark">
          <img src={src} alt="Logo on dark" onError={onError} />
        </div>
        <div className="faint" style={{ fontSize: 12, marginTop: 6, textAlign: 'center' }}>On dark</div>
      </div>
    </div>
  );
}

// ── Splash ─────────────────────────────────────────────────────────────────────────

/** What the browser could establish about a chosen file, before anything is sent. */
interface Measured {
  readonly file: File;
  readonly mime: SplashAssetMime;
  readonly objectUrl: string;
  readonly isVideo: boolean;
  /** Intrinsic pixels, or null when the browser could not decode the file at all. */
  readonly width: number | null;
  readonly height: number | null;
  /** A refusal the server will also make. Non-null means the upload button stays disabled. */
  readonly refusal: string | null;
  /** Worth saying, but not a refusal — a landscape video, say. */
  readonly caution: string | null;
}

/**
 * Read a file's intrinsic dimensions in the browser.
 *
 * An `Image` for a still and a `<video>` for a clip, both fed the same object URL, because
 * `naturalWidth` is meaningless on a video element and `videoWidth` does not exist on an
 * image. Either can fail — a corrupt file, a codec the browser will not decode — and a
 * failure resolves to null rather than rejecting: the point is to *save* a round-trip, not to
 * become a second gatekeeper, and a file this cannot measure is one the server should be
 * allowed to judge for itself.
 */
function measureDimensions(url: string, isVideo: boolean): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (isVideo) {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => resolve({ width: video.videoWidth, height: video.videoHeight });
      video.onerror = () => resolve(null);
      video.src = url;
      return;
    }
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

/**
 * Everything the browser can check before a byte is sent, phrased as the server would phrase
 * it — the type, the ceiling, and the portrait rule.
 *
 * The portrait rule is an **image** rule on both sides. The API reads dimensions out of a
 * PNG/JPEG/WebP header and refuses a landscape still; it does not attempt a video, because a
 * clip's aspect lives in a `tkhd` box inside `moov` and that is real container parsing. So a
 * landscape video gets a caution here rather than a refusal — claiming to enforce a rule the
 * server does not would leave an operator unable to upload something that is actually accepted.
 */
async function measureSplash(file: File): Promise<Measured | { error: string }> {
  if (!isSplashMime(file.type)) {
    return {
      error:
        'A splash must be PNG, JPEG, WebP or MP4. SVG is not accepted: it is an active document that can carry script, and these bytes are served publicly and rendered.',
    };
  }
  if (file.size > SPLASH_MAX_BYTES) {
    return {
      error: `That file is ${megabytes(file.size)}; the limit is ${megabytes(SPLASH_MAX_BYTES)}. The whole asset travels to the database in one packet, so the ceiling is a hard one rather than a suggestion.`,
    };
  }

  const isVideo = file.type === 'video/mp4';
  const objectUrl = URL.createObjectURL(file);
  const dimensions = await measureDimensions(objectUrl, isVideo);

  let refusal: string | null = null;
  let caution: string | null = null;
  if (dimensions === null) {
    caution = isVideo
      ? 'This browser could not read the video’s dimensions. The upload will still be checked by the server.'
      : 'This browser could not decode the image, so its dimensions could not be checked here. The server will read them from the file header and refuse a landscape image.';
  } else if (!isVideo && dimensions.width > dimensions.height) {
    refusal =
      `The splash must be portrait, and this image is ${dimensions.width}×${dimensions.height} (landscape).` +
      ' It is rendered full-bleed on a phone at launch, where a landscape image letterboxes or crops' +
      ' its subject out. Re-export it taller than it is wide.';
  } else if (isVideo && dimensions.width > dimensions.height) {
    caution =
      `This video is ${dimensions.width}×${dimensions.height} (landscape). The server does not check a` +
      ' video’s aspect, because it cannot read one without parsing the container, so this will upload. It will' +
      ' still letterbox on a phone, so a portrait export is worth the trouble.';
  }

  return { file, mime: file.type, objectUrl, isVideo, width: dimensions?.width ?? null, height: dimensions?.height ?? null, refusal, caution };
}

function SplashSection({ canWrite }: { canWrite: boolean }): JSX.Element {
  const { api } = useAuth();
  const toast = useToast();
  const state = useAsync(() => api.brandSplash(), []);
  /**
   * The settings row, read alongside the asset, because the two answer different questions
   * and the console must not conflate them. `GET /admin/brand/splash` says what bytes are
   * *stored*; `platform_settings.splashAssetKey` says whether the app is being *pointed* at
   * them. Clearing the splash sets that pointer to null and leaves the bytes where they are —
   * so a page that only read the asset would go on showing a splash it had just turned off.
   */
  const settings = useAsync(() => api.getBusinessSettings(), []);

  const inputRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Measured | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    return () => {
      if (picked !== null) URL.revokeObjectURL(picked.objectUrl);
    };
  }, [picked]);

  function reset(): void {
    if (picked !== null) URL.revokeObjectURL(picked.objectUrl);
    setPicked(null);
    setPickError(null);
    if (inputRef.current) inputRef.current.value = '';
  }

  async function choose(file: File | undefined): Promise<void> {
    if (file === undefined) return;
    const measured = await measureSplash(file);
    if (picked !== null) URL.revokeObjectURL(picked.objectUrl);
    if ('error' in measured) {
      setPicked(null);
      setPickError(measured.error);
      return;
    }
    setPickError(null);
    setPicked(measured);
  }

  async function upload(): Promise<void> {
    if (picked === null || picked.refusal !== null) return;
    setBusy(true);
    try {
      const dataBase64 = await toBase64(picked.file);
      const updated = await api.uploadBrandSplash({ mime: picked.mime, dataBase64 });
      state.setData(() => updated);
      // The upload points the settings row at the asset in the same transaction, so the
      // local copy has to follow or the "not in use" note below would be a lie.
      settings.setData((prev) => (prev === null ? prev : { ...prev, splashAssetKey: updated.key }));
      reset();
      toast.success('Splash uploaded', 'The app shows it on the next launch. The public config is refreshed immediately.');
    } catch (err) {
      toast.error('Upload failed', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Stop showing the splash.
   *
   * The only mechanism the API offers, and deliberately so: the upload route sets the pointer
   * as a side effect of storing bytes, so without a writable `splashAssetKey` there would be
   * no way back to no splash at all. The bytes stay in `brand_assets` — this is not a delete,
   * and the card says as much rather than implying the file is gone.
   */
  async function clearSplash(): Promise<void> {
    setBusy(true);
    try {
      const updated = await api.setBusinessSettings({ splashAssetKey: null });
      settings.setData(() => updated);
      toast.success('Splash cleared', 'The app shows no splash. The uploaded file is still stored and can be pointed at again.');
    } catch (err) {
      toast.error('Could not clear the splash', messageOf(err));
    } finally {
      setBusy(false);
    }
  }

  if (state.loading || settings.loading) return <LoadingState label="Loading the splash screen…" />;
  // The settings read failing is worth reporting, but it must not take the upload control
  // with it: an operator can still replace the splash without knowing whether it is in use.
  if (state.error !== null) return <ErrorBox message={state.error} />;

  const current = state.data;
  const inUse = current !== null && settings.data?.splashAssetKey === current.key;

  return (
    <div className="grid grid--2" style={{ marginTop: 16 }}>
      <Card
        title="App splash screen"
        actions={
          current === null ? (
            <Badge tone="neutral">None set</Badge>
          ) : inUse ? (
            <Badge tone="green" dot>
              Showing in the app
            </Badge>
          ) : (
            <Badge tone="amber" dot>
              Stored, not in use
            </Badge>
          )
        }
      >
        <p className="muted" style={{ marginTop: -4 }}>
          The first thing a customer sees, drawn full-bleed while the app starts. A portrait
          image, or a short MP4. Short means short: a clip that outlasts the launch it is
          covering is a delay, not a brand moment.
        </p>

        <div
          className={`dropzone${dragOver ? ' dropzone--over' : ''}`}
          role="button"
          tabIndex={0}
          onClick={() => canWrite && inputRef.current?.click()}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && canWrite) inputRef.current?.click();
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (canWrite) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (canWrite) void choose(e.dataTransfer.files[0]);
          }}
        >
          <div style={{ display: 'grid', placeItems: 'center', gap: 8 }}>
            <IconFilm size={30} />
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>
              {picked?.file.name ?? 'Drop a splash here, or click to browse'}
            </div>
            <div className="faint" style={{ fontSize: 12 }}>
              PNG · JPG · WebP · MP4 · portrait · max {megabytes(SPLASH_MAX_BYTES)}
            </div>
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,video/mp4"
            hidden
            disabled={!canWrite}
            onChange={(e) => void choose(e.target.files?.[0])}
          />
        </div>

        {pickError !== null && <ErrorBox message={pickError} />}

        {picked !== null && (
          <div className="splash-check">
            <div className="splash-check__row">
              <Badge tone={picked.isVideo ? 'violet' : 'blue'}>
                {picked.mime.replace('image/', '').replace('video/', '').toUpperCase()}
              </Badge>
              <span className="mono">{megabytes(picked.file.size)}</span>
              <span className="mono">
                {picked.width === null || picked.height === null
                  ? 'dimensions unread'
                  : `${picked.width}×${picked.height}`}
              </span>
              {picked.width !== null && picked.height !== null && picked.width <= picked.height && (
                <Badge tone="green">Portrait</Badge>
              )}
            </div>
            {picked.refusal !== null && (
              <div className="error-box" role="alert">
                <IconAlert size={16} />
                <div>{picked.refusal}</div>
              </div>
            )}
            {picked.caution !== null && <p className="splash-check__note">{picked.caution}</p>}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          <Button
            variant="primary"
            loading={busy}
            disabled={!canWrite || picked === null || picked.refusal !== null}
            onClick={() => void upload()}
          >
            {current === null ? 'Set splash screen' : 'Replace splash'}
          </Button>
          {picked !== null && (
            <Button variant="ghost" disabled={busy} onClick={reset}>
              Cancel
            </Button>
          )}
          {inUse && (
            <Button
              variant="danger"
              disabled={!canWrite || busy}
              onClick={() => void clearSplash()}
              title="Point the app at no splash. The uploaded file stays stored."
            >
              Clear splash
            </Button>
          )}
        </div>

        {current === null ? (
          <div className="kv" style={{ marginTop: 20 }}>
            <dt>Stored asset</dt>
            <dd className="faint">Nothing uploaded yet, so the app starts on its plain background.</dd>
          </div>
        ) : (
          <div className="kv" style={{ marginTop: 20 }}>
            <dt>Stored asset</dt>
            <dd className="mono" style={{ fontSize: 12 }}>{current.key}</dd>
            <dt>Format</dt>
            <dd>
              <Badge tone="neutral">
                {current.mime.replace('image/', '').replace('video/', '').toUpperCase()}
              </Badge>
            </dd>
            <dt>Size</dt>
            <dd className="mono">{megabytes(current.bytes)}</dd>
            <dt>Dimensions</dt>
            <dd className="mono">
              {current.width === null || current.height === null ? (
                // Null for a video, always — the API does not parse a container to find out,
                // and says so rather than inventing a figure.
                <span className="faint">
                  {current.mime === 'video/mp4' ? 'not read for a video' : 'not recorded'}
                </span>
              ) : (
                `${current.width}×${current.height}`
              )}
            </dd>
            <dt>Updated</dt>
            <dd>{relativeTime(current.updatedAt)}</dd>
            <dt>In use</dt>
            <dd>
              {inUse ? (
                'Yes. The app is pointed at this asset.'
              ) : (
                <span className="muted">
                  No. The bytes are stored but Business Settings points at nothing, so the app shows
                  no splash. Upload again to point it back at one.
                </span>
              )}
            </dd>
          </div>
        )}

        {settings.error !== null && (
          <p className="splash-check__note">
            Whether the app is currently pointed at this asset could not be read: {settings.error}
          </p>
        )}
      </Card>

      <Card title="Splash preview">
        <SplashPreview picked={picked} current={current} inUse={inUse} />
      </Card>
    </div>
  );
}

/**
 * The phone-shaped preview.
 *
 * Shows the operator's *selection* when there is one, and otherwise the asset the server
 * already holds — fetched from the public `/config/splash`, which is where the app itself
 * gets it, so what is on screen is genuinely what a customer would see. In a mock build there
 * is no API origin and therefore nothing to fetch; that is stated rather than left as a
 * broken image.
 *
 * A video is muted, looping and `playsInline` for the same reason the app plays it that way:
 * an autoplaying splash with sound is a hostile launch experience, and without `playsInline`
 * mobile Safari takes the video fullscreen.
 */
function SplashPreview({
  picked,
  current,
  inUse,
}: {
  picked: Measured | null;
  current: SplashAsset | null;
  inUse: boolean;
}): JSX.Element {
  if (picked !== null) {
    return (
      <>
        <div className="splash-frame">
          {picked.isVideo ? (
            <video src={picked.objectUrl} muted loop playsInline autoPlay />
          ) : (
            <img src={picked.objectUrl} alt="Splash preview" />
          )}
        </div>
        <p className="splash-frame__note">
          Your selection, not yet uploaded. Drawn to fill a portrait phone, exactly as the app
          draws it.
        </p>
      </>
    );
  }

  if (current === null) {
    return (
      <div className="faint" style={{ padding: '20px 0' }}>
        No splash yet. Pick a file to see it drawn on a phone.
      </div>
    );
  }

  if (API_BASE_URL === '') {
    return (
      <div className="faint" style={{ padding: '20px 0' }}>
        A splash is stored ({current.mime}), but this console is running on its in-memory demo
        backend, so there are no bytes to fetch. Pick a file to preview one.
      </div>
    );
  }

  // Version-stamped on the asset's own timestamp, the same way `/config` stamps it, so
  // replacing the splash busts the browser cache instead of showing yesterday's.
  const src = assetUrl(API_BASE_URL, '/config/splash', current.updatedAt);
  return (
    <>
      <StoredSplash src={src} isVideo={current.mime === 'video/mp4'} />
      <p className="splash-frame__note">
        {inUse
          ? 'Live. This is what a customer sees while the app starts.'
          : 'Stored, but the app is pointed at no splash, so a customer sees none of this.'}
      </p>
    </>
  );
}

/**
 * The stored splash inside its phone frame, or a statement that it would not load.
 *
 * The failure panel is drawn *instead of* the frame rather than inside it: `.splash-frame` is a
 * 9:19.5 box with `overflow: hidden` and a sunken background, so a message placed in it would
 * be clipped to a phone shape — and an empty phone outline is exactly the "uploaded, renders
 * nothing" appearance this is here to replace.
 */
function StoredSplash({ src, isVideo }: { src: string | null; isVideo: boolean }): JSX.Element {
  const { failed, onError } = useAssetFailure(src);

  if (src === null) return <></>;
  if (failed) return <AssetFailure label="The splash screen" src={src} />;

  return (
    <div className="splash-frame">
      {isVideo ? (
        <video src={src} muted loop playsInline autoPlay onError={onError} />
      ) : (
        <img src={src} alt="Current splash screen" onError={onError} />
      )}
    </div>
  );
}
